import React from "react";
import { Form } from "@components";
import { Wrapper } from "../wrapper";

const SYSTEMS_OPTIONS = [
  { key: "gmasNorthAmerica", label: "GMAS North America" },
  { key: "tmasNorthAmerica", label: "TMAS North America" },
  { key: "passportNorthAmerica", label: "Passport North America" },
  { key: "readOnly", label: "Read Only" },
] as const;

type SystemKey = typeof SYSTEMS_OPTIONS[number]["key"];

type InternalValues = Record<SystemKey, boolean>;

export type ArcsSettlementFormValues = {
  systems: string;
};

type Props = {
  onCancel: () => void;
  onSubmit: (values: ArcsSettlementFormValues) => void;
  open: boolean;
  values: Partial<ArcsSettlementFormValues>;
  readonly?: boolean;
};

function deserializeSystems(systems: string | undefined): InternalValues {
  const selected = systems ? systems.split(",").map((s) => s.trim()) : [];
  return {
    gmasNorthAmerica: selected.includes("GMAS North America"),
    passportNorthAmerica: selected.includes("Passport North America"),
    tmasNorthAmerica: selected.includes("TMAS North America"),
    readOnly: selected.includes("Read Only"),
  };
}

function serializeSystems(internal: InternalValues): string {
  return SYSTEMS_OPTIONS.filter((opt) => internal[opt.key])
    .map((opt) => opt.label)
    .join(",");
}

export const ArcsSettlementForm: React.FC<Props> = ({
  onCancel,
  onSubmit,
  open,
  values,
  readonly,
}) => {
  const initialInternal = deserializeSystems(values?.systems);

  const form = Form.useManager<InternalValues>({
    ...initialInternal,
  });

  React.useEffect(() => {
    form.set(Form.target.VALUES, deserializeSystems(values?.systems));
  }, [values?.systems]);

  function onCheckboxChange(name: string, value: boolean): void {
    form.set(Form.target.VALUE, [name as SystemKey, value]);
    // clear any "at least one" error when a box is checked
    if (value) {
      form.set(Form.target.ERROR, ["gmasNorthAmerica", undefined]);
    }
  }

  function validate(internal: InternalValues): Error | undefined {
    const anySelected = SYSTEMS_OPTIONS.some((opt) => internal[opt.key]);
    return anySelected
      ? undefined
      : new Error("At least one system must be selected");
  }

  function onFormSubmit(): void {
    const error = validate(form.values);
    if (!error) {
      onSubmit({ systems: serializeSystems(form.values) });
    } else {
      form.set(Form.target.ERROR, ["gmasNorthAmerica", error]);
    }
  }

  const selectionError = form.errors.gmasNorthAmerica;

  return (
    <Wrapper
      onCancel={() => {
        onCancel();
        form.set(Form.target.VALUES, deserializeSystems(values?.systems));
      }}
      onSubmit={onFormSubmit}
      open={open}
      title="ARCs Settlement (Account Reconciliation Cloud)"
      readonly={readonly}
    >
      <Form>
        <Form.Title error={selectionError} subtitle="Select all that applies:">
          Security Access group
        </Form.Title>
        {SYSTEMS_OPTIONS.map((opt) => (
          <Form.Checkbox
            key={opt.key}
            name={opt.key}
            label={opt.label}
            onChange={onCheckboxChange}
            size={6}
            value={form.values[opt.key]}
            disabled={readonly}
          />
        ))}
        <Form.Error error={selectionError} />
      </Form>
    </Wrapper>
  );
};
